import React from "react"
import { compose } from "redux"
import { useTranslation } from "react-i18next"
import myInitObject from "../../../../modules/GlobalConstants"
import { withRouter, RouteComponentProps } from "react-router"
import { Button, Paper, Table, TableBody, TableCell, TableHead, TablePagination, TableRow } from "@material-ui/core"
// HOCs
import withInProgressRecord, { WithInProgressRecordProps } from "../../hoc/withInProgressRecord"
import withDoctorAvailableRecords, { WithDoctorAvailableRecordsType } from "../../hoc/withDoctorAvailableRecords"

// Styles
import useStyles from "./styles"

// Models
import { Record } from "../../../records/models"

// Constants
import { AppRoute } from "../../../routing/constants"

// Utils
import DateUtils from "../../../../utils/DateUtils"
import { hasRecordAction, isRecordActionDisabled, recordActionKind } from "../../utils/recordActions"
import withSpeedAndAmplitude, { WithSpeedAndAmplitudeProps } from "../../../records/hoc/withSpeedAndAmplitude"

function AvailableRecordTable(props: WithDoctorAvailableRecordsType & RouteComponentProps & WithInProgressRecordProps & WithSpeedAndAmplitudeProps) {
    const {
        fetchAvailableRecords,
        availableRecords,
        availableRecordsTotal,
        availableRecordsCurrentPage,
        availableRecordsSize,
        setAvailableRecordsPage,
        setAvailableRecordsSize,
        history,
        inProgressRecord,
        setMMPerMv,
        setMMPerSecond
    } = props;
    const [t] = useTranslation()
    const styles = useStyles()

    React.useEffect(() => {
        fetchAvailableRecords()
    }, [fetchAvailableRecords]);

    // The `myInitObject.data == "true"` reload that used to sit here is gone —
    // `doctorUnassignedWSEvent` refetches the queue through the websocket epic
    // now. See ../../../ws/store/epics.

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setAvailableRecordsPage(page)
    }

    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setAvailableRecordsSize(e.target.value)
        setAvailableRecordsPage(0)
    }

    const handleContinue = (id: Record["teleRecordId"]) => {
        myInitObject.FetchRecordStatus = "true";
        history.push(AppRoute.Record, { id });
    }
    const handleAssignClick = (id: Record["teleRecordId"]) => {
        setMMPerMv(10);
        setMMPerSecond(25);
        history.push(AppRoute.Record, { id });
    }

    return (
        <Paper className={styles.container}>
            <Table className={styles.table}>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            {t("stats.available_records_table.name_column")}
                        </TableCell>
                        <TableCell align="right">
                            {t("stats.available_records_table.severity_column")}
                        </TableCell>
                        <TableCell align="right">
                            {t("stats.available_records_table.time_column")}
                        </TableCell>
                        <TableCell align="right">
                            {t("stats.available_records_table.country_column")}
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>

                    {availableRecords.map((record, index) => {
                        return (
                            <TableRow key={record.teleRecordId}>
                                <TableCell component="th" scope="row">{record.fileName}</TableCell>
                                <TableCell align="right">
                                    {t(`stats.available_records_table.${record.normality}`)}
                                </TableCell>
                                <TableCell align="right">
                                    {DateUtils.toStringFormat("yyyy-MM-dd hh:mm", DateUtils.getDateISOString(record.time.toString()))}
                                </TableCell>
                                <TableCell align="right">{record.folder}</TableCell>
                                <TableCell align="right">
                                    {hasRecordAction(record) && (
                                        recordActionKind(record) === "continue" ? (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                disabled={isRecordActionDisabled(record, inProgressRecord)}
                                                onClick={() => handleContinue(record.teleRecordId)}
                                            >
                                                {t("stats.continue")}
                                            </Button>
                                        ) : (
                                            <Button
                                                size="medium"
                                                variant="outlined"
                                                color="primary"
                                                disabled={isRecordActionDisabled(record, inProgressRecord)}
                                                onClick={() => handleAssignClick(record.teleRecordId)}
                                            >
                                                {t("stats.available_records_table.assign_button")}
                                            </Button>
                                        )
                                    )}
                                </TableCell>
                            </TableRow>
                        )

                    })}
                    {availableRecordsTotal === 0 && (
                        <TableRow>
                            <TableCell colSpan={6} align="center">
                                {t("stats.available_records_table.no_data")}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            {
                availableRecordsTotal > 0 && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10]}
                        component="div"
                        className={styles.pagination}
                        count={availableRecordsTotal}
                        labelRowsPerPage={`${t("stats.available_records_table.labelRowsPerPage")}:`}
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to === -1 ? count : to} ${t("stats.available_records_table.labelDisplayedRows")} ${count}`}
                        rowsPerPage={availableRecordsSize}
                        page={availableRecordsCurrentPage}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                )
            }
        </Paper >
    )
}

export default compose(
    withDoctorAvailableRecords,
    withRouter,
    withInProgressRecord,
    withSpeedAndAmplitude,
)(AvailableRecordTable)
